import React, { useEffect, useState } from "react";
import axios from "../services/api";
import toast from "react-hot-toast";
import { FaTrash, FaPlusCircle, FaEdit, FaSave, FaTimes } from "react-icons/fa";

export default function AdminAuthorizedReports() {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [authorized, setAuthorized] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedReports, setSelectedReports] = useState([]);

  const [editUserId, setEditUserId] = useState(null);
  const [editReports, setEditReports] = useState([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [cRes, uRes, rRes, aRes] = await Promise.all([
        axios.get("/companies/"),
        axios.get("/users/"),
        axios.get("/reports/"),
        axios.get("/authorized-reports/"),
      ]);
      setCompanies(cRes.data);
      setUsers(uRes.data);
      setReports(rRes.data);
      setAuthorized(aRes.data);
    } catch {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleAddAuthorization = async () => {
    if (!selectedUser || selectedReports.length === 0) {
      toast.error("Please select a user and at least one report");
      return;
    }

    try {
      await Promise.all(
        selectedReports.map((report_id) =>
          axios.post("/authorized-reports/", {
            user_id: selectedUser,
            report_id,
          })
        )
      );
      toast.success("Authorizations added");
      fetchInitialData();
      setSelectedReports([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    }
  };

  const handleRemoveAllForUser = async (userId) => {
    const userAuths = authorized.filter((a) => a.user_id === userId);
    try {
      await Promise.all(
        userAuths.map((a) => axios.delete(`/authorized-reports/${a.id}`))
      );
      toast.success("All authorizations removed for user");
      fetchInitialData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleEdit = (userId) => {
    const userAuths = authorized
      .filter((a) => a.user_id === userId)
      .map((a) => a.report_id);
    setEditUserId(userId);
    setEditReports(userAuths);
  };

  const handleSaveEdit = async () => {
    const oldAuths = authorized.filter((a) => a.user_id === editUserId);
    const toDelete = oldAuths.filter((a) => !editReports.includes(a.report_id));
    const toAdd = editReports.filter(
      (rid) => !oldAuths.some((a) => a.report_id === rid)
    );

    try {
      await Promise.all([
        ...toDelete.map((a) => axios.delete(`/authorized-reports/${a.id}`)),
        ...toAdd.map((report_id) =>
          axios.post("/authorized-reports", {
            user_id: editUserId,
            report_id,
          })
        ),
      ]);
      toast.success("Authorizations updated");
      setEditUserId(null);
      setEditReports([]);
      fetchInitialData();
    } catch {
      toast.error("Failed to update authorizations");
    }
  };

  const filteredUsers = selectedCompany
    ? users.filter((u) => u.company_id === selectedCompany)
    : users;

  const filteredReports = selectedCompany
    ? reports.filter((r) => r.company_id === selectedCompany)
    : reports;

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">
        🔐 Authorized Reports
      </h2>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <select
          className="border p-2 rounded w-full"
          value={selectedCompany}
          onChange={(e) => {
            setSelectedCompany(e.target.value);
            setSelectedUser("");
            setSelectedReports([]);
          }}
        >
          <option value="">All Companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          className="border p-2 rounded w-full"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
        >
          <option value="">Select User</option>
          {filteredUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} ({u.email})
            </option>
          ))}
        </select>

        <select
          multiple
          className="border p-2 rounded w-full h-32"
          value={selectedReports}
          onChange={(e) =>
            setSelectedReports(
              Array.from(e.target.selectedOptions, (opt) => opt.value)
            )
          }
        >
          {filteredReports.map((r) => (
            <option key={r.id} value={r.id}>
              {r.title} ({r.department})
            </option>
          ))}
        </select>

        <button
          onClick={handleAddAuthorization}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded flex items-center justify-center gap-2"
          disabled={loading}
        >
          <FaPlusCircle /> Add Authorizations
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center my-6">
          <div className="h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl shadow-md">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Reports</th>
                <th className="px-4 py-2">Company</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const userAuths = authorized.filter((a) => a.user_id === user.id);
                const company = companies.find((c) => c.id === user.company_id);
                const isEditing = editUserId === user.id;

                return (
                  <tr key={user.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {user.name}{" "}
                      <span className="text-xs text-gray-500">({user.email})</span>
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <div className="flex flex-wrap gap-2">
                          {filteredReports.map((r) => (
                            <label
                              key={r.id}
                              className="text-xs border px-2 py-1 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="mr-1"
                                checked={editReports.includes(r.id)}
                                onChange={() => {
                                  setEditReports((prev) =>
                                    prev.includes(r.id)
                                      ? prev.filter((id) => id !== r.id)
                                      : [...prev, r.id]
                                  );
                                }}
                              />
                              {r.title}
                            </label>
                          ))}
                        </div>
                      ) : (
                        userAuths.map((a) => {
                          const r = reports.find((r) => r.id === a.report_id);
                          return (
                            <span
                              key={a.id}
                              className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-1 mb-1"
                            >
                              {r?.title}
                            </span>
                          );
                        })
                      )}
                    </td>
                    <td className="px-4 py-2">{company?.name || "Unknown"}</td>
                    <td className="px-4 py-2 text-right space-x-3">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            className="text-green-600 hover:text-green-800"
                          >
                            <FaSave className="inline-block mr-1" /> Save
                          </button>
                          <button
                            onClick={() => {
                              setEditUserId(null);
                              setEditReports([]);
                            }}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <FaTimes className="inline-block mr-1" /> Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(user.id)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <FaEdit className="inline-block mr-1" /> Edit
                          </button>
                          <button
                            onClick={() => handleRemoveAllForUser(user.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <FaTrash className="inline-block mr-1" /> Remove
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
